import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, KeyRound, Play, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, errMsg, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Empty, Loader, PageHeader } from "@/components/cast/Common";
import { Emblem, EmblemPicker } from "@/components/cast/Emblem";
import { FamilyMap } from "@/components/cast/FamilyMap";
import { FamilyVerseBook } from "@/components/cast/FamilyVerseBook";
import { CatchGarden } from "@/components/cast/CatchGarden";

function PinCard() {
  const { user, refresh } = useAuth();
  const [pin, setPin] = useState("");
  const save = async () => { try { await api.post("/parent/pin", { pin }); setPin(""); await refresh(); toast.success("Parent PIN saved"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="card-warm p-6" data-testid="parent-pin-card">
      <div className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#C86D51]" /><h3 className="font-display text-2xl font-semibold">Parent PIN</h3></div>
      <p className="mt-2 text-sm text-slate-600">{user.has_pin ? "Your PIN is set. Children need it to leave Kids Cove." : "Set a 4-digit PIN before opening Kids Cove. It's required to exit back to the adult area."}</p>
      <div className="mt-4 flex gap-2">
        <Input data-testid="parent-pin-input" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="••••" inputMode="numeric" className="w-28 bg-white text-center tracking-[0.5em]" />
        <Button data-testid="parent-pin-save-button" disabled={pin.length !== 4} onClick={save} className="rounded-full bg-[#1E293B]">{user.has_pin ? "Change PIN" : "Set PIN"}</Button>
      </div>
    </div>
  );
}

function AddChild({ onAdded }: { onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState<any>({ name: "", age: "8", avatar: { icon: "fish", color: "#C86D51" } });
  const submit = async () => { try { await api.post("/children", { ...f, age: +f.age }); setOpen(false); setF({ ...f, name: "" }); onAdded(); toast.success("Child profile added"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="parent-add-child-button" className="rounded-full bg-[#C86D51] hover:bg-[#B35A3F]"><Plus className="mr-1 h-4 w-4" />Add child</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display text-2xl">Add a child profile</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>First name or nickname</Label><Input data-testid="child-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Age</Label><Select value={f.age} onValueChange={(v) => setF({ ...f, age: v })}><SelectTrigger data-testid="child-age-select" className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{Array.from({ length: 11 }, (_, i) => `${i + 5}`).map((a) => <SelectItem key={a} value={a}>{a} years</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Emblem</Label><div className="mt-2"><EmblemPicker prefix="child-emblem" value={f.avatar} onChange={(a) => setF({ ...f, avatar: a })} /></div></div>
          <p className="text-xs text-slate-500">Children can only use the Kids Cove learning area. They can't post, comment, message, or see the community feed.</p>
          <Button data-testid="child-save-button" disabled={!f.name.trim()} onClick={submit} className="w-full rounded-full bg-[#1E293B]">Save profile</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Parent() {
  const { user, enterKids } = useAuth();
  const nav = useNavigate();
  const [kids, setKids] = useState<any[] | null>(null);
  const load = () => api.get("/children").then((r) => setKids(r.data));
  useEffect(() => { load(); }, []);
  const enter = (c: any) => {
    if (!user.has_pin) return toast.error("Please set a parent PIN first");
    enterKids({ id: c.id, name: c.name, age: c.age, avatar: c.avatar });
    nav("/kids");
  };
  const del = async (id: string) => { await api.delete(`/children/${id}`); load(); };

  return (
    <div>
      <PageHeader metaphor="Kids Cove" label="Parent Dashboard" title="Guide their first steps." subtitle="Create child profiles (ages 5–15), open the child-safe learning area, and see what they've completed."><AddChild onAdded={load} /></PageHeader>
      <div className="grid gap-8 lg:grid-cols-12">
        <div className="min-w-0 lg:col-span-4"><PinCard /></div>
        <div className="min-w-0 space-y-6 lg:col-span-8">
          {kids === null ? <Loader /> : kids.length === 0 ? <Empty testId="children-empty" title="No child profiles yet" body="Add a child to open Kids Cove for them." /> : kids.map((c) => (
            <div key={c.id} className="card-warm p-6" data-testid={`child-card-${c.id}`}>
              <div className="flex flex-wrap items-center gap-4">
                <Emblem avatar={c.avatar} size={56} />
                <div className="flex-1"><h3 className="font-display text-2xl font-semibold">{c.name}</h3><p className="text-sm text-slate-500">Age {c.age} · {c.activity.length} stories completed</p></div>
                <Button data-testid={`enter-kids-${c.id}`} onClick={() => enter(c)} className="rounded-full bg-[#81B29A] text-white hover:bg-[#6c9c85]"><Play className="mr-1 h-4 w-4" />Open Kids Cove</Button>
                <Button variant="ghost" size="icon" data-testid={`delete-child-${c.id}`} onClick={() => del(c.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
              {c.activity.length > 0 && (
                <ul className="mt-5 space-y-2 border-t border-[#E2DBCE] pt-4" data-testid={`child-activity-${c.id}`}>
                  {c.activity.slice(0, 5).map((a: any) => <li key={a.episode_id} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 className="h-4 w-4 text-[#5B7065]" />{a.episode_title}<span className="text-slate-400">· quiz {a.quiz_score}/{a.quiz_total} · {timeAgo(a.completed_at)}</span></li>)}
                </ul>
              )}
            </div>
          ))}
          {kids && kids.length > 0 && <FamilyMap />}
          {kids && kids.length > 0 && (
            <section className="space-y-4" data-testid="parent-gardens">
              <h2 className="font-display text-2xl font-semibold text-slate-900">Catch gardens</h2>
              {kids.map((k: any) => <CatchGarden key={k.id} kidId={k.id} name={k.name} parent />)}
            </section>
          )}
          <FamilyVerseBook />
        </div>
      </div>
    </div>
  );
}
